<template>
  <div class="max-w-7xl mx-auto p-6">
    <!-- Header Section -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 mb-2">
            Banner Manager
          </h1>
          <p class="text-gray-600">
            Manage and toggle banner visibility for your website.
          </p>
        </div>

        <div class="flex space-x-3">
          <button
            @click="showModalWithJSON = true"
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <i class="fas fa-code mr-2"></i>
            View JSON
          </button>

          <button
            @click="addNewBanner()"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <i class="fas fa-plus mr-2"></i>
            Add Banner
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="mb-6">
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium text-gray-700"
              >Filter by status:</span
            >
            <div class="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                @click="statusFilter = 'all'"
                :class="[
                  'px-4 py-2 text-sm font-medium transition-colors duration-200',
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                ]"
              >
                All ({{ banners.length }})
              </button>
              <button
                @click="statusFilter = 'active'"
                :class="[
                  'px-4 py-2 text-sm font-medium border-l border-gray-200 transition-colors duration-200',
                  statusFilter === 'active'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                ]"
              >
                Active ({{ activeBannersCount }})
              </button>
              <button
                @click="statusFilter = 'inactive'"
                :class="[
                  'px-4 py-2 text-sm font-medium border-l border-gray-200 transition-colors duration-200',
                  statusFilter === 'inactive'
                    ? 'bg-gray-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                ]"
              >
                Inactive ({{ inactiveBannersCount }})
              </button>
            </div>
          </div>

          <!-- Results count -->
          <div class="text-sm text-gray-500 ml-auto">
            Showing {{ filteredBanners.length }} of {{ banners.length }} banners
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
      ></div>
    </div>

    <!-- Banners Grid -->
    <div
      v-else-if="filteredBanners.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      <!-- Banner Cards -->
      <div
        v-for="(banner, index) in filteredBanners"
        :key="banner.id || index"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
      >
        <!-- Banner Image -->
        <div class="relative h-48 overflow-hidden">
          <img
            :src="banner.url"
            :alt="banner.title"
            class="w-full h-full object-cover"
            @error="handleImageError"
          />

          <!-- Status Badge -->
          <div class="absolute top-3 left-3">
            <span
              :class="{
                'bg-green-100 text-green-800': banner.status == '1',
                'bg-red-100 text-red-800': banner.status == '0'
              }"
              class="px-2 py-1 text-xl font-semibold rounded-full"
            >
              {{ banner.status == "1" ? "Active" : "Inactive" }}
            </span>
          </div>

          <!-- Action Buttons (more prominent and visible) -->
          <div
            class="absolute top-2 right-2 opacity-75 group-hover:opacity-100 transition-all duration-200"
          >
            <div
              class="flex space-x-1 bg-black/20 backdrop-blur-sm rounded-lg p-1"
            >
              <button
                @click="toggleBannerStatus(banner)"
                class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                :title="
                  banner.status == '1' ? 'Deactivate Banner' : 'Activate Banner'
                "
              >
                <span class="text-xs">
                  {{ banner.status == "1" ? "⏸️" : "▶️" }}
                </span>
              </button>

              <button
                @click="editBanner = banner"
                class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                title="Edit Banner"
              >
                <span class="text-xs">✏️</span>
              </button>

              <button
                @click="removeBanner(banner)"
                class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center"
                title="Delete Banner"
              >
                <span class="text-xs">🗑️</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Banner Info -->
        <div class="p-4">
          <h3 class="font-semibold text-gray-900 mb-2 line-clamp-2">
            {{ banner.title }}
          </h3>

          <div class="flex items-center justify-between text-sm text-gray-500">
            <span>ID: {{ banner.id }}</span>
            <span v-if="banner.updatedAt">
              Updated: {{ formatDate(banner.updatedAt) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <div class="text-gray-400 text-6xl mb-4">
        {{ banners.length === 0 ? "🖼️" : "🔍" }}
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        {{ banners.length === 0 ? "No banners yet" : "No banners found" }}
      </h3>
      <p class="text-gray-500 mb-6">
        {{
          banners.length === 0
            ? "Create your first banner to get started."
            : `No ${statusFilter} banners found. Try changing the filter or create a new banner.`
        }}
      </p>
      <div class="flex justify-center space-x-3">
        <button
          v-if="banners.length > 0 && statusFilter !== 'all'"
          @click="statusFilter = 'all'"
          class="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Show All Banners
        </button>
        <button
          @click="addNewBanner()"
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          <span class="text-sm mr-2">➕</span>
          {{
            banners.length === 0 ? "Add Your First Banner" : "Add New Banner"
          }}
        </button>
      </div>
    </div>

    <!-- Modals -->
    <BaseModal v-model="showModalWithJSON" class="json-modal">
      <template v-slot:header>
        Frontend JSON Configuration
      </template>
      <div class="p-4" v-if="bannersJson">
        <vueJsonEditor
          v-model="bannersJson"
          :show-btns="false"
          :expandedOnStart="true"
          @json-save="onJsonSaved"
        />
      </div>
    </BaseModal>

    <EditBannerModal
      v-if="editBanner"
      :banner="editBanner"
      @closedBannerEdition="editBanner = null"
      @updatedBanner="updatedBanner"
      @createdBanner="createdBanner"
    />
  </div>
</template>
<script>
import vueJsonEditor from "vue-json-editor";
import {
  getBanners,
  requestDeleteBanner,
  updateBanner
} from "@/api/store/BannersService.js";
import EditBannerModal from "@/components/shared/EditBannerModal.vue";
import { getSettings, updateSetting } from "@/api/store/SettingsService.js";
// import { debounce } from "lodash";
export default {
  components: {
    EditBannerModal,
    vueJsonEditor
  },
  data() {
    return {
      banners: [],
      editBanner: null,
      bannersJson: null,
      showModalWithJSON: false,
      loading: false,
      statusFilter: "all" // 'all', 'active', 'inactive'
    };
  },
  computed: {
    filteredBanners() {
      if (this.statusFilter === "all") {
        return this.banners;
      } else if (this.statusFilter === "active") {
        return this.banners.filter(banner => banner.status === "1");
      } else if (this.statusFilter === "inactive") {
        return this.banners.filter(banner => banner.status === "0");
      }
      return this.banners;
    },
    activeBannersCount() {
      return this.banners.filter(banner => banner.status === "1").length;
    },
    inactiveBannersCount() {
      return this.banners.filter(banner => banner.status === "0").length;
    }
  },
  mounted() {
    this.loadBanners();
  },
  watch: {
    async showModalWithJSON(val) {
      if (val) {
        const vals = await getSettings("banners");
        this.bannersJson = JSON.parse(vals.value);
      }
    }
  },
  methods: {
    async onJsonSaved() {
      await updateSetting("banners", JSON.stringify(this.bannersJson), 1);
      this.$toast.open({
        message: `Setting updated`,
        type: "success",
        position: "top-right"
      });
    },
    async loadBanners() {
      this.loading = true;
      try {
        const response = await getBanners();
        this.banners = this.handleResponse(response) || [];
      } catch (error) {
        console.error("Error loading banners:", error);
        this.$toast.open({
          message: "Error loading banners",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    createdBanner(response) {
      const responseData = this.handleResponse(response);
      const banner = responseData.banner || responseData;

      this.editBanner = banner;
      this.banners.unshift(banner);

      this.$toast.open({
        message: "Banner created successfully",
        type: "success",
        position: "top-right"
      });
    },
    updatedBanner(response) {
      // Handle the camelized response with banner data
      const responseData = this.handleResponse(response);
      const banner = responseData.banner || responseData;

      const existingIndex = this.banners.findIndex(b => b.id == banner.id);
      if (existingIndex !== -1) {
        this.$set(this.banners, existingIndex, banner);
      }

      this.$toast.open({
        message: "Banner updated successfully",
        type: "success",
        position: "top-right"
      });
    },
    async removeBanner(banner) {
      if (
        confirm(
          "Are you sure you want to delete this banner? This action can't be reversed."
        )
      ) {
        try {
          const response = await requestDeleteBanner(banner.id);
          const responseData = this.handleResponse(response);

          if (responseData.status === "success") {
            this.banners = this.banners.filter(x => x.id !== banner.id);
            this.$toast.open({
              message: "Banner deleted successfully",
              type: "success",
              position: "top-right"
            });
          } else {
            this.$toast.open({
              message: responseData.message || "Error deleting banner",
              type: "error",
              position: "top-right"
            });
          }
        } catch (error) {
          console.error("Error deleting banner:", error);
          this.$toast.open({
            message: "Error deleting banner",
            type: "error",
            position: "top-right"
          });
        }
      }
    },
    addNewBanner() {
      this.editBanner = {
        title: "",
        id: null,
        status: "0",
        url: null
      };
    },

    async toggleBannerStatus(banner) {
      const newStatus = banner.status === "1" ? "0" : "1";
      const action = newStatus === "1" ? "activate" : "deactivate";

      if (confirm(`Are you sure you want to ${action} this banner?`)) {
        try {
          await updateBanner(banner.id, { status: newStatus });

          const index = this.banners.findIndex(b => b.id === banner.id);
          if (index !== -1) {
            this.$set(this.banners, index, {
              ...this.banners[index],
              status: newStatus
            });
          }

          this.$toast.open({
            message: `Banner ${action}d successfully`,
            type: "success",
            position: "top-right"
          });
        } catch (error) {
          console.error("Error toggling banner status:", error);
          this.$toast.open({
            message: `Error ${action}ing banner`,
            type: "error",
            position: "top-right"
          });
        }
      }
    },

    handleImageError(event) {
      event.target.src =
        "https://via.placeholder.com/400x200?text=Image+Not+Found";
      event.target.alt = "Image not found";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    handleResponse(response) {
      if (response && response.data) {
        return response.data;
      }
      return response;
    }
  }
};
</script>
<style lang="scss">
.category-edit-modal {
  &.modal {
    .modal-content {
      height: auto;
      width: 80%;
      padding: 0px;
    }
  }
}
</style>
<style lang="scss" scoped>
@import "@/assets/style/css/App.scss";
.list {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  .box {
    margin: 10px;
    height: 200px;
    width: 400px;
    overflow: hidden;
    padding: 5px;
    @extend .shadow-4dp;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-size: 15px;
    text-align: center;
    position: relative;
    .options {
      display: none;
    }
    &:hover {
      .options {
        display: block;
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        flex-direction: row;
        .icon {
          border: 1px solid $M8;
          border-radius: 10px;
          padding: 5px;
          width: 30px;
          margin-left: 5px;
          &:hover {
            background-color: $M8;
          }
          img {
            width: 100%;
          }
        }
      }
    }
    .banner-img {
      width: 100%;
    }
    .title {
      margin-top: 0px;
      margin-bottom: 10px;
    }
  }
}
</style>
